import { useState, useEffect } from "react";
import { Check, X, AlertTriangle } from "lucide-react";

/**
 * Modal that shows a mini seat plan for a reservation and lets the cashier
 * tick individual seats. Used twice in the guided flow:
 *
 *  - "BATCH PICKER" mode: which of the open seats are being handed out right now?
 *  - "STORNO" mode: which of the open seats should be cancelled (released)?
 *
 * Props:
 *  - open: bool
 *  - title: string
 *  - subtitle: string
 *  - seats: array of "row-seat" strings (the seats that ARE selectable)
 *  - inBatch: optional array of "row-seat" already in the current batch (shown
 *            but not selectable here)
 *  - initialSelected: array of "row-seat" pre-selected
 *  - confirmLabel: string
 *  - confirmStyle: "primary" | "danger"
 *  - perSeatConfirm: if true, ask "Sicher?" on every tap (storno UX)
 *  - onConfirm(seats[])
 *  - onClose()
 */
export default function SeatPickerModal({
  open,
  title,
  subtitle,
  seats = [],
  inBatch = [],
  initialSelected = [],
  confirmLabel = "Weiter",
  confirmStyle = "primary",
  perSeatConfirm = false,
  emptyHint = "Keine Sitze verfügbar.",
  onConfirm,
  onClose,
}) {
  const [selected, setSelected] = useState(() => new Set(initialSelected));
  const [pendingConfirm, setPendingConfirm] = useState(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setSelected(new Set(initialSelected));
      setPendingConfirm(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const inBatchSet = new Set(inBatch);
  const seatList = [...seats].sort((a, b) => {
    const [ar, as_] = a.split("-").map(Number);
    const [br, bs_] = b.split("-").map(Number);
    return ar - br || as_ - bs_;
  });

  // group seats by row for layout
  const byRow = new Map();
  for (const key of seatList) {
    const [r, s] = key.split("-").map(Number);
    if (!byRow.has(r)) byRow.set(r, []);
    byRow.get(r).push(s);
  }
  for (const arr of byRow.values()) arr.sort((a, b) => a - b);

  const toggleSeat = (key) => {
    if (perSeatConfirm) {
      setPendingConfirm(key);
      return;
    }
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const confirmPending = () => {
    if (!pendingConfirm) return;
    if (onConfirm) onConfirm([pendingConfirm]);
    setPendingConfirm(null);
  };
  const cancelPending = () => setPendingConfirm(null);

  const selectAll = () => setSelected(new Set(seatList));
  const selectNone = () => setSelected(new Set());

  const confirmAll = () => {
    if (perSeatConfirm) return; // not used in storno mode
    onConfirm(Array.from(selected));
  };

  const colorClass = confirmStyle === "danger"
    ? "bg-red-600 hover:bg-red-700"
    : "bg-emerald-600 hover:bg-emerald-700";

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="seat-picker-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b-2 border-zinc-100 flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-black tracking-tight">{title}</h2>
            {subtitle && <p className="text-zinc-500 text-base mt-1">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="h-10 w-10 rounded-full hover:bg-zinc-100 flex items-center justify-center" data-testid="seat-picker-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {!perSeatConfirm && (
          <div className="px-6 py-3 border-b border-zinc-100 flex items-center gap-2 bg-zinc-50">
            <button onClick={selectAll} data-testid="seat-picker-all"
              className="h-11 px-4 rounded-lg border-2 border-zinc-200 hover:bg-white font-bold text-sm">
              Alle auswählen
            </button>
            <button onClick={selectNone} data-testid="seat-picker-none"
              className="h-11 px-4 rounded-lg border-2 border-zinc-200 hover:bg-white font-bold text-sm">
              Keinen
            </button>
            <span className="ml-auto text-base font-bold text-zinc-700">
              {selected.size} von {seatList.length} ausgewählt
            </span>
          </div>
        )}

        <div className="px-6 py-5 overflow-y-auto flex-1">
          {seatList.length === 0 ? (
            <p className="text-base text-zinc-500 text-center py-8">{emptyHint}</p>
          ) : (
            <div className="space-y-2">
              {[...byRow.keys()].sort((a, b) => a - b).map((rowNum) => (
                <div key={rowNum} className="flex items-center gap-2">
                  <span className="font-mono text-sm font-bold text-zinc-500 w-12 shrink-0">Reihe {rowNum}</span>
                  <div className="flex flex-wrap gap-2">
                    {byRow.get(rowNum).map((s) => {
                      const key = `${rowNum}-${s}`;
                      const isSelected = selected.has(key);
                      const isInBatch = inBatchSet.has(key);
                      return (
                        <button
                          key={key}
                          data-testid={`picker-seat-${key}`}
                          onClick={() => !isInBatch && toggleSeat(key)}
                          disabled={isInBatch}
                          className={`h-14 w-14 rounded-lg font-mono font-black text-base border-2 transition ${
                            isInBatch
                              ? "bg-zinc-200 border-zinc-300 text-zinc-400 cursor-not-allowed"
                              : isSelected
                                ? (confirmStyle === "danger"
                                    ? "bg-red-500 border-red-600 text-white"
                                    : "bg-emerald-500 border-emerald-600 text-white")
                                : "bg-white border-zinc-300 hover:bg-zinc-50 text-zinc-700"
                          }`}>
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {inBatch.length > 0 && (
            <p className="mt-4 text-sm text-zinc-500">
              Grau hinterlegt = wird gerade kassiert (nicht stornierbar).
            </p>
          )}
        </div>

        {!perSeatConfirm && (
          <div className="px-6 py-4 border-t-2 border-zinc-100 flex gap-3">
            <button onClick={onClose} data-testid="seat-picker-cancel"
              className="flex-1 h-16 rounded-xl border-2 border-zinc-200 hover:bg-zinc-50 font-bold text-base">
              Abbrechen
            </button>
            <button onClick={confirmAll} data-testid="seat-picker-confirm"
              disabled={selected.size === 0}
              className={`flex-1 h-16 rounded-xl ${colorClass} disabled:opacity-40 text-white font-black text-lg`}>
              {confirmLabel} ({selected.size})
            </button>
          </div>
        )}

        {/* Per-seat confirmation overlay (storno mode) */}
        {perSeatConfirm && pendingConfirm && (
          <div className="absolute inset-0 bg-zinc-950/60 flex items-center justify-center p-4 rounded-2xl" data-testid="storno-confirm-overlay">
            <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 text-center">
              <div className="h-14 w-14 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-3">
                <AlertTriangle className="h-7 w-7 text-red-700" />
              </div>
              <h3 className="text-xl font-black mb-2">Sitz wirklich stornieren?</h3>
              <p className="text-base text-zinc-600 mb-5">
                Sitz <strong className="font-mono">{pendingConfirm}</strong> wird im Saalplan wieder frei.
              </p>
              <div className="flex gap-2">
                <button onClick={cancelPending} data-testid="storno-cancel"
                  className="flex-1 h-14 rounded-lg border-2 border-zinc-200 hover:bg-zinc-50 font-bold">
                  Abbrechen
                </button>
                <button onClick={confirmPending} data-testid="storno-confirm"
                  className="flex-1 h-14 rounded-lg bg-red-600 hover:bg-red-700 text-white font-black">
                  Ja, stornieren
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
